import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, LogOut } from 'lucide-react';
import { Restaurant, ParseResponse, Vendor } from './types';
import { OrderParserHeader, OrderInput } from './components';
import { useAuth } from '@/shared/contexts';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.giotheapp.com';

const SupplierOrderParser: React.FC = () => {
  const {
    user,
    accessToken,
    isAuthenticated,
    isLoading: isAuthLoading,
    logout
  } = useAuth();
  const navigate = useNavigate();

  // Get URL params
  const urlParams = new URLSearchParams(window.location.search);
  const supplierIdFromUrl = urlParams.get('supplierId');

  const getToken = useCallback(() => {
    return accessToken || '';
  }, [accessToken]);

  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] =
    useState<Restaurant | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [orderText, setOrderText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedUnits] = useState<Record<number, string>>({});
  const [itemVendorAssignments] = useState<Record<number, string>>({});
  const [step, setStep] = useState<'parse' | 'success'>('parse');
  const [initialized, setInitialized] = useState(false);

  // Initialize - fetch restaurants
  useEffect(() => {
    if (isAuthLoading || initialized) return;

    const fetchData = async () => {
      const token = getToken();
      if (!token) return;

      try {
        const restUrl = supplierIdFromUrl
          ? `${API_URL}/api/users/supplierRestaurantList?supplierId=${supplierIdFromUrl}`
          : `${API_URL}/api/users/supplierRestaurantList`;

        const restResp = await axios.get<{ data: Restaurant[] }>(restUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRestaurants(restResp.data.data);

        if (restResp.data.data.length === 1) {
          setSelectedRestaurant(restResp.data.data[0]);
          setStep('parse');
        }

        // Fetch vendors
        // const vendorUrl = supplierIdFromUrl
        //     ? `${API_URL}/api/users/vendorList?supplierId=${supplierIdFromUrl}`
        //     : user?.supplier_id
        //         ? `${API_URL}/api/users/vendorList?supplier_id=${user.supplier_id}`
        //         : null;
        const vendorUrl = user?.id
          ? `${API_URL}/api/users/vendorList?supplier_id=${user.id}`
          : null;
        console.log('Vendor URL:', vendorUrl);
        if (vendorUrl) {
          try {
            const vendorResp = await axios.get<{ data: Vendor[] }>(vendorUrl, {
              headers: { Authorization: `Bearer ${token}` }
            });

            setVendors(vendorResp.data.data);
          } catch {
            // Vendors are optional
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load data');
      }
    };

    fetchData();
    setInitialized(true);
  }, [isAuthLoading, supplierIdFromUrl, getToken, user, initialized]);

  const handleParse = async () => {
    const trimmed = orderText.trim();
    if (!trimmed) {
      setError('Please enter some order text first');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const requestBody = {
        text: trimmed,
        supplierId: user?.supplier_id || supplierIdFromUrl || '',
        restaurantId: selectedRestaurant?.id
      };

      const response = await axios.post<ParseResponse>(
        `${API_URL}/api/orders/parse-order?supplierId=${user?.id || supplierIdFromUrl}`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          }
        }
      );
      // Navigate to results page with data
      navigate('/supplier-order-results', {
        state: {
          result: response.data,
          editableItems: response.data.items,
          vendors,
          selectedUnits,
          itemVendorAssignments,
          selectedRestaurant
        }
      });
    } catch (err: unknown) {
      console.error('Parse error:', err);
      const message = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setError(message || 'Failed to parse order');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setOrderText('');
    setError(null);
    setStep('parse');
  };

  // Show loading
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  // Show auth error
  if (!isAuthenticated) {
    return (
      <div className="light min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
        <OrderParserHeader
          step="parse"
          selectedRestaurant={null}
          onNewOrder={() => {}}
        />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 mb-6">Please log in.</p>
            <a
              href="/login"
              className="inline-block px-6 py-3 bg-[#006B82] text-white rounded-xl hover:bg-[#005A6E]"
            >
              Log In
            </a>
          </div>
        </main>
      </div>
    );
  }

  // Show dropdown above input area
  const renderRestaurantDropdown = () => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Select Restaurant <span className="text-red-500">*</span>
      </label>
      <select
        value={selectedRestaurant?.id || ''}
        onChange={e => {
          const restaurant = restaurants.find(r => r.id === e.target.value);
          if (restaurant) {
            setSelectedRestaurant(restaurant);
          }
        }}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#006B82] focus:border-[#006B82] bg-white"
      >
        <option value="">-- Select a Restaurant --</option>
        {restaurants.map(restaurant => (
          <option key={restaurant.id} value={restaurant.id}>
            {restaurant.business_name}
          </option>
        ))}
      </select>
      {error && !selectedRestaurant && (
        <p className="mt-1 text-sm text-red-600">Please select a restaurant</p>
      )}
    </div>
  );

  // Main render
  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="light min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      <div className="bg-gradient-to-r from-[#006B82] to-[#00809C] text-white px-3 sm:px-6 py-3 flex justify-between items-center">
        <OrderParserHeader
          step={step === 'success' ? 'success' : 'parse'}
          selectedRestaurant={selectedRestaurant}
          onNewOrder={handleClear}
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {step === 'parse' && (
        <main className="flex-1 flex flex-col items-center md:flex-row px-3 sm:px-4 md:px-6 pt-2  md:py-8 gap-4 md:gap-6 lg:gap-8">
          <div className="w-full max-w-4xl mx-auto px-2  sm:px-6">
            {renderRestaurantDropdown()}
            <OrderInput
              orderText={orderText}
              onOrderTextChange={setOrderText}
              onParse={handleParse}
              onClear={handleClear}
              loading={loading}
              error={error}
            />
          </div>
        </main>
      )}
    </div>
  );
};

export default SupplierOrderParser;
